import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle2, Heart, ArrowLeft, IndianRupee, Building2, Copy, Upload, QrCode } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const DonorRegistrationPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    fatherOrHusbandName: '',
    nukh: '',
    address: '',
    phone: '',
    email: '',
    membershipType: '',
    amount: 0,
    declaration: false,
    additionalComments: '',
  });

  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const bankDetails = {
    accountName: 'Brahmkshatriya Marwari Khatri Association',
    bankName: 'Bank of India',
    accountNumber: '661020110000985',
    ifscCode: 'BKID0006610',
    branch: 'Chopasni Road, Jodhpur'
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleMembershipSelect = (type) => {
    const amount = type === 'shiksha_mitra' ? 5100 : 11000;
    setFormData({ ...formData, membershipType: type, amount });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('फ़ाइल का आकार 10MB से कम होना चाहिए');
        return;
      }
      setPaymentReceipt(file);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.membershipType) {
      toast.error('कृपया सदस्यता श्रेणी चुनें');
      return;
    }

    if (!formData.declaration) {
      toast.error('कृपया घोषणा पर सहमति दें');
      return;
    }

    setLoading(true);

    try {
      // Create form data for submission
      const submitData = {
        ...formData,
        paymentMethod: 'Bank Transfer/UPI',
        city: '',
        state: '',
        pincode: '',
        occupation: '',
        whatsapp: '',
        transactionId: '',
        paymentDate: '',
        message: formData.additionalComments
      };

      await axios.post(`${API}/scholarship/donor-registration`, submitData);
      toast.success('पंजीकरण सफलतापूर्वक जमा हो गया!');
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'पंजीकरण विफल। कृपया पुनः प्रयास करें।');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Card className="p-12 shadow-floating">
            <div className="mb-6">
              <CheckCircle2 className="w-20 h-20 text-green-600 mx-auto mb-4" />
              <h1 className="font-serif font-bold text-3xl text-primary mb-4">
                पंजीकरण सफल!
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                {formData.membershipType === 'shiksha_mitra' ? 'शिक्षा मित्र' : 'शिक्षा परममित्र'} के रूप में आपके पंजीकरण के लिए धन्यवाद।
              </p>
              <div className="bg-muted p-6 rounded-lg mb-6 text-left">
                <p className="text-sm text-foreground mb-2">
                  <strong>आगे क्या होगा?</strong>
                </p>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• आपके भुगतान का सत्यापन किया जाएगा</li>
                  <li>• सत्यापन के बाद आपको पुष्टि भेजी जाएगी</li>
                  <li>• आपको "{formData.membershipType === 'shiksha_mitra' ? 'शिक्षा मित्र' : 'शिक्षा परममित्र'}" की उपाधि से सम्मानित किया जाएगा</li>
                  <li>• किसी भी प्रश्न के लिए संपर्क करें: brahmkhatrimahasabha@gmail.com</li>
                </ul>
              </div>
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => (window.location.href = '/')}
                  className="bg-primary hover:bg-primary/90"
                >
                  होम पेज पर जाएं
                </Button>
                <Link to="/scholarships">
                  <Button variant="outline">
                    छात्रवृत्ति पेज देखें
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/scholarships" className="inline-flex items-center text-primary hover:text-primary/80 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          छात्रवृत्ति पेज पर वापस जाएं
        </Link>

        <Card className="p-8 md:p-12 shadow-floating">
          {/* Header */}
          <div className="text-center mb-10 border-b-2 border-secondary pb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/20 rounded-full mb-4">
              <Heart className="w-8 h-8 text-secondary" />
            </div>
            <h1 className="font-serif font-bold text-2xl md:text-3xl text-primary mb-3">
              महासभा नीड कम मैरिट लोन स्कॉलरशिप
            </h1>
            <h2 className="font-serif font-bold text-xl md:text-2xl text-secondary mb-4">
              Permanent Member Consent
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
              महासभा नीड कम मैरिट स्कॉलरशिप योजना में "शिक्षा मित्र" / "शिक्षा परममित्र" के रूप में स्थायी सदस्य बनने हेतु कृपया नीचे दी गई जानकारी भरें एवं वार्षिक योगदान जमा करें।
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">
                  Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="example@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="fullName">
                  Full Name (पूरा नाम) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="अपना पूरा नाम दर्ज करें"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="fatherOrHusbandName">
                  Father's / Husband's Name (पिता / पति का नाम) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="fatherOrHusbandName"
                  name="fatherOrHusbandName"
                  type="text"
                  placeholder="पिता / पति का नाम"
                  value={formData.fatherOrHusbandName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="nukh">
                  Nukh (नुख) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="nukh"
                  name="nukh"
                  type="text"
                  placeholder="अपना नुख दर्ज करें"
                  value={formData.nukh}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="address">
                  Complete Residential Address (पूरा स्थायी पता) <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="address"
                  name="address"
                  rows={3}
                  placeholder="मकान नं., गली, मोहल्ला, शहर, राज्य, पिनकोड"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">
                  Mobile Number (मोबाइल नंबर) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="10 अंकों का मोबाइल नंबर"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Membership Selection */}
            <div className="space-y-4">
              <Label>
                Select Membership Category (सदस्यता श्रेणी चुनें) <span className="text-destructive">*</span>
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  onClick={() => handleMembershipSelect('shiksha_mitra')}
                  className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.membershipType === 'shiksha_mitra'
                      ? 'border-secondary bg-secondary/10 shadow-md'
                      : 'border-border hover:border-secondary/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      formData.membershipType === 'shiksha_mitra' ? 'border-secondary' : 'border-muted-foreground'
                    }`}>
                      {formData.membershipType === 'shiksha_mitra' && (
                        <div className="w-3 h-3 rounded-full bg-secondary"></div>
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">शिक्षा मित्र</h4>
                      <p className="text-2xl font-bold text-secondary">₹5,100/-</p>
                    </div>
                  </div>
                </div>

                <div
                  onClick={() => handleMembershipSelect('shiksha_parammitra')}
                  className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.membershipType === 'shiksha_parammitra'
                      ? 'border-primary bg-primary/10 shadow-md'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      formData.membershipType === 'shiksha_parammitra' ? 'border-primary' : 'border-muted-foreground'
                    }`}>
                      {formData.membershipType === 'shiksha_parammitra' && (
                        <div className="w-3 h-3 rounded-full bg-primary"></div>
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">शिक्षा परममित्र</h4>
                      <p className="text-2xl font-bold text-primary">₹11,000/-</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Details Section */}
            <div className="space-y-4 bg-blue-50 p-6 rounded-xl border border-blue-200">
              <h3 className="font-serif font-bold text-xl text-primary flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Payment Details (भुगतान विवरण)
              </h3>
              
              <Card className="p-6 bg-white">
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                    <span className="text-sm text-muted-foreground">Account Name:</span>
                    <span className="font-medium text-sm">{bankDetails.accountName}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                    <span className="text-sm text-muted-foreground">Bank Name:</span>
                    <span className="font-medium">{bankDetails.bankName}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                    <span className="text-sm text-muted-foreground">Account Number:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium font-mono">{bankDetails.accountNumber}</span>
                      <button 
                        type="button"
                        onClick={() => copyToClipboard(bankDetails.accountNumber)}
                        className="text-primary hover:text-primary/80"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                    <span className="text-sm text-muted-foreground">IFSC Code:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium font-mono">{bankDetails.ifscCode}</span>
                      <button 
                        type="button"
                        onClick={() => copyToClipboard(bankDetails.ifscCode)}
                        className="text-primary hover:text-primary/80"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                    <span className="text-sm text-muted-foreground">Branch:</span>
                    <span className="font-medium">{bankDetails.branch}</span>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-border">
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <QrCode className="w-4 h-4" />
                      <span>Scan with any UPI App (Google Pay / PhonePe / Paytm)</span>
                    </div>
                    <img
                      src="https://customer-assets.emergentagent.com/job_abcfafc4-09e8-4929-917e-c56a46d51339/artifacts/bbdulxwl_Mahasabha%20QR%20code.jpeg"
                      alt="Mahasabha UPI QR Code"
                      className="w-56 h-auto rounded-lg border border-border shadow-sm bg-white p-2"
                      data-testid="donor-upi-qr-code"
                    />
                    <p className="text-xs text-muted-foreground text-center">
                      किसी भी UPI ऐप से QR कोड स्कैन करें
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Payment Receipt Upload */}
            <div className="space-y-4">
              <Label htmlFor="paymentReceipt">
                Upload Payment Receipt (भुगतान रसीद अपलोड करें) <span className="text-destructive">*</span>
              </Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-2">
                  Upload 1 supported file: PDF, document, or image. Max 10 MB.
                </p>
                <Input
                  id="paymentReceipt"
                  name="paymentReceipt"
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="max-w-xs mx-auto"
                />
                {paymentReceipt && (
                  <p className="text-sm text-green-600 mt-2">✓ {paymentReceipt.name}</p>
                )}
              </div>
            </div>

            {/* Declaration */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary">
                Declaration (घोषणा) <span className="text-destructive">*</span>
              </h3>
              <div className="bg-secondary/5 p-6 rounded-lg border-2 border-secondary/20">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="declaration"
                    checked={formData.declaration}
                    onChange={handleChange}
                    className="w-5 h-5 mt-0.5"
                    required
                  />
                  <span className="text-foreground leading-relaxed">
                    मैं घोषणा करता/करती हूँ कि मैंने अपनी इच्छा से "शिक्षा मित्र" / "शिक्षा परममित्र" सदस्यता हेतु स्वीकृति दी है। मैं वार्षिक योगदान रु. 5,100 / रु. 11,000 (जैसा चयनित है) <strong>कम से कम सात वर्षों</strong> तक समय पर जमा करूंगा/करूंगी। मैंने छात्रवृत्ति नीति को पढ़ा एवं स्वीकार किया है।
                  </span>
                </label>
              </div>
            </div>

            {/* Additional Comments */}
            <div>
              <Label htmlFor="additionalComments">
                Additional Comments (यदि कोई हो)
              </Label>
              <Textarea
                id="additionalComments"
                name="additionalComments"
                rows={3}
                placeholder="कोई अतिरिक्त संदेश या टिप्पणी..."
                value={formData.additionalComments}
                onChange={handleChange}
              />
            </div>

            {/* Amount Display */}
            {formData.membershipType && (
              <div className="bg-primary/10 p-6 rounded-lg text-center">
                <p className="text-lg text-muted-foreground mb-2">देय राशि:</p>
                <p className="text-4xl font-bold text-primary">
                  ₹{formData.amount.toLocaleString('en-IN')}/-
                </p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-center pt-6">
              <Button
                type="submit"
                className="bg-secondary hover:bg-secondary/90 text-white font-serif text-lg px-12 py-6 w-full md:w-auto"
                disabled={loading || !formData.membershipType || !formData.declaration}
              >
                {loading ? 'जमा हो रहा है...' : 'Submit / जमा करें'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default DonorRegistrationPage;
